import { useMemo, useState } from 'react';

import {
  Badge,
  Button,
  Caption,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Icon,
  Input,
  Stack,
  Text,
  Textarea,
  cn,
} from '@eluve/components';
import {
  TRANSCRIPT_SPECIALTIES,
  TranscriptLibraryEntry,
  TranscriptLibraryGroup,
  TranscriptSpecialty,
  groupedTranscriptLibrary,
} from '@eluve/llm-outputs';

interface TranscriptLibraryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUseTranscript: (text: string) => void;
}

/**
 * The journey-grouped synthetic transcript library (ENG-5403, ported from
 * Orchard's injection picker). A two-pane dialog: a searchable, specialty-
 * filterable list grouped by patient journey (visits in order, single-visit
 * examples in their own trailing section), and an editable preview. "Use
 * transcript" hands the (possibly edited) text back to the caller — injection
 * itself stays in AddToTranscript, unchanged.
 */
export const TranscriptLibraryDialog: React.FC<
  TranscriptLibraryDialogProps
> = ({ open, onOpenChange, onUseTranscript }) => {
  const [text, setText] = useState('');
  // The library entry currently loaded into the preview (for selection
  // styling). Cleared once the text is edited away from the original.
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) {
      setText('');
      setSelectedId(null);
    }
    onOpenChange(nextOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent size="4xl">
        <DialogHeader>
          <DialogTitle>Transcript library</DialogTitle>
          <DialogDescription>
            Pick a visit from a patient journey or a single-visit example,
            review and edit it, then load it into the transcript box.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 sm:grid-cols-2">
          <TranscriptLibraryPicker
            selectedId={selectedId}
            onPick={(entry) => {
              setText(entry.text);
              setSelectedId(entry.id);
            }}
          />
          <Stack gap={2} align="stretch">
            <Caption color="secondary">Preview (editable)</Caption>
            <Textarea
              value={text}
              onChange={(e) => {
                setText(e.target.value);
                setSelectedId(null);
              }}
              placeholder="Pick an entry from the library to preview it here..."
              className="max-h-[56vh] min-h-[300px] flex-1 resize-none text-sm text-text-primary"
            />
          </Stack>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => handleOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={!text.trim()}
            onClick={() => {
              onUseTranscript(text);
              handleOpenChange(false);
            }}
          >
            Use transcript
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

interface TranscriptLibraryPickerProps {
  selectedId: string | null;
  onPick: (entry: TranscriptLibraryEntry) => void;
}

const TranscriptLibraryPicker: React.FC<TranscriptLibraryPickerProps> = ({
  selectedId,
  onPick,
}) => {
  const [query, setQuery] = useState('');
  const [activeSpecialties, setActiveSpecialties] = useState<
    TranscriptSpecialty[]
  >([]);

  const toggleSpecialty = (specialty: TranscriptSpecialty) =>
    setActiveSpecialties((prev) =>
      prev.includes(specialty)
        ? prev.filter((s) => s !== specialty)
        : [...prev, specialty],
    );

  const groups = useMemo(
    () => groupedTranscriptLibrary(query, activeSpecialties),
    [query, activeSpecialties],
  );

  const shownCount = groups.reduce((n, g) => n + g.entries.length, 0);

  return (
    <Stack gap={3} align="stretch" className="min-h-0">
      <div className="relative">
        <Icon
          name="Search"
          size="xs"
          className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-text-tertiary"
        />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search patient, condition, or tag..."
          className="pl-9"
        />
      </div>

      <Stack direction="horizontal" gap={1.5} className="flex-wrap">
        {TRANSCRIPT_SPECIALTIES.map((specialty) => {
          const isActive = activeSpecialties.includes(specialty);
          return (
            <button
              key={specialty}
              type="button"
              aria-pressed={isActive}
              onClick={() => toggleSpecialty(specialty)}
              className={cn(
                'rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                isActive
                  ? 'border-transparent bg-bg-accent-strong-primary text-text-accent-strong'
                  : 'border-border-primary text-text-secondary hover:bg-bg-secondary',
              )}
            >
              {specialty}
            </button>
          );
        })}
        <Text
          size="xs"
          color="tertiary"
          inline
          className="ml-auto tabular-nums"
        >
          {shownCount} transcript{shownCount === 1 ? '' : 's'}
        </Text>
      </Stack>

      <Stack
        gap={2.5}
        align="stretch"
        className="max-h-[48vh] min-h-[300px] overflow-y-auto pr-1"
      >
        {groups.length === 0 ? (
          <Text size="xs" color="tertiary" className="py-6 text-center">
            No transcripts match your filters.
          </Text>
        ) : (
          groups.map((group) => (
            <TranscriptLibrarySection
              key={group.journey?.id ?? 'single-visits'}
              group={group}
              selectedId={selectedId}
              onPick={onPick}
            />
          ))
        )}
      </Stack>
    </Stack>
  );
};

interface TranscriptLibrarySectionProps {
  group: TranscriptLibraryGroup;
  selectedId: string | null;
  onPick: (entry: TranscriptLibraryEntry) => void;
}

const TranscriptLibrarySection: React.FC<TranscriptLibrarySectionProps> = ({
  group: { journey, entries },
  selectedId,
  onPick,
}) => (
  <section className="shrink-0 overflow-hidden rounded-lg border border-border-primary">
    <header className="border-b border-border-primary bg-bg-secondary px-3 py-2">
      <Stack direction="horizontal" gap={2} justify="between">
        <Stack
          direction="horizontal"
          gap={2}
          className="min-w-0"
          fullWidth={false}
        >
          <Text size="sm" weight="semibold" inline className="truncate">
            {journey?.title ?? 'Single visits'}
          </Text>
          {journey && <Badge variant="secondary">{journey.specialty}</Badge>}
        </Stack>
        <Text
          size="xs"
          color="tertiary"
          inline
          className="shrink-0 tabular-nums"
        >
          {entries.length}{' '}
          {journey
            ? `visit${entries.length === 1 ? '' : 's'}`
            : `example${entries.length === 1 ? '' : 's'}`}
        </Text>
      </Stack>
      <Text size="xs" color="tertiary" className="mt-0.5 truncate">
        {journey?.subtitle ?? 'Standalone example conversations'}
      </Text>
    </header>
    <ul className="divide-y divide-border-primary">
      {entries.map((entry) => (
        <li key={entry.id}>
          <button
            type="button"
            onClick={() => onPick(entry)}
            data-selected={selectedId === entry.id}
            className="flex w-full items-start gap-2.5 px-3 py-2 text-left transition-colors hover:bg-bg-secondary data-[selected=true]:bg-bg-tertiary"
          >
            {entry.visit !== undefined && (
              <span
                className="mt-0.5 grid size-5 shrink-0 place-items-center rounded-full bg-bg-tertiary text-2xs font-semibold text-text-secondary tabular-nums"
                aria-hidden
              >
                {entry.visit}
              </span>
            )}
            <Stack gap={0.5} align="stretch" className="min-w-0 flex-1">
              <Stack direction="horizontal" gap={2} justify="between">
                <Text size="xs" weight="medium" inline>
                  {entry.title}
                </Text>
                {entry.variant ? (
                  <Badge
                    variant={entry.variant === 'rich' ? 'secondary' : 'outline'}
                  >
                    {entry.variant}
                  </Badge>
                ) : (
                  <Badge variant="outline">{entry.specialty}</Badge>
                )}
              </Stack>
              <Text size="xs" color="tertiary" className="line-clamp-2">
                {entry.summary}
              </Text>
              {entry.phase && (
                <Caption size="3xs" color="tertiary">
                  {entry.phase}
                </Caption>
              )}
            </Stack>
          </button>
        </li>
      ))}
    </ul>
  </section>
);
